import React, { useCallback, useEffect, useRef, useState } from "react"
import Logo from "@ciq/shared/components/logo"
import Api from "@ciq/shared/api/api"
import { Banner } from "@ciq/shared/components/views"
import { ContactAdminModal } from "@ciq/shared/components/modal"
import { useAppContext } from "@ciq/shared/hooks/context"
import { LoadingStatus } from "@ciq/shared/api/loading"

const LOADING_ID = "console:load"

export function Console({ onContactClick }) {
  const {
    schoolId,
    user,
    views,
    landingViewLoadingState,
    addLoadingId,
    removeLoadingId,
    isLoadingKey,
  } = useAppContext()

  const [url, setUrl] = useState(null)
  const [error, setError] = useState(null)
  const [viewRequestModal, setViewRequestModal] = useState(false)

  const addLoadingIdRef = useRef(addLoadingId)
  const removeLoadingIdRef = useRef(removeLoadingId)

  useEffect(() => {
    if (
      schoolId == null ||
      !views.can_view_dashboard ||
      landingViewLoadingState !== LoadingStatus.FETCHED
    ) {
      return
    }

    addLoadingIdRef.current(LOADING_ID)

    const controller = new AbortController()
    Api.get(`/console/${schoolId}`, { signal: controller.signal })
      .then(response => {
        setUrl(response.data.url)
        setError(null)
      })
      .catch(e => {
        setError("Unable to load dashboard")

        if (e.handled) return

        console.error(e.error)
      })
      .finally(() => {
        removeLoadingIdRef.current(LOADING_ID)
      })

    return () => controller.abort()
  }, [schoolId, views.can_view_dashboard, landingViewLoadingState])

  const handleOnContact = useCallback(
    message => {
      if (!onContactClick) {
        hideContactModal()
        return
      }

      const loadingId = "console:message"
      addLoadingIdRef.current(loadingId)
      onContactClick(message, successful => {
        removeLoadingIdRef.current(loadingId)
        if (successful) {
          hideContactModal()
        }
      })
    },
    [onContactClick]
  )

  if (isLoadingKey(LOADING_ID) || landingViewLoadingState !== LoadingStatus.FETCHED) {
    return null
  }

  const showContactModal = () => setViewRequestModal(true)
  const hideContactModal = () => setViewRequestModal(false)

  if (!views.can_view_dashboard) {
    return (
      <div className="da-quicksight-request-rights">
        <div className="da-quicksight-request-rights-banner">
          <Banner>
            You do not have the permissions to view this page.{" "}
            <span onClick={showContactModal}>Contact Admin</span> to request view rights.
          </Banner>
        </div>
        <div className="da-quicksight-request-rights-container">
          <Logo />
          <div className="da-card">
            <h1>Request Access</h1>
            <div>
              <span>You do not have permissions to view this page</span>
            </div>
          </div>
        </div>
        <ContactAdminModal
          show={viewRequestModal}
          title="Request Access"
          placeholderOptions={{
            message: `Hi! This is ${user.name} and I'm requesting permissions to be a Dashboard viewer`,
          }}
          onHide={hideContactModal}
          onContact={handleOnContact}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-container">
        <div>
          <span>{error}</span>
        </div>
      </div>
    )
  }

  return (
    <iframe
      className="da-quicksight-console"
      title="Quicksight Console"
      width="100%"
      height="100%"
      src={url}
    />
  )
}
